import {dsvFormat} from 'd3';
import {parseDateInput} from 'helpers/dates';

export type RoadmapIssueType =
  | 'Company Narrative'
  | 'Narrative Workstream'
  | 'Sub-Workstream'
  | 'Roadmap Initiative';

export interface RoadmapAncestryItem {
  key: string;
  title: string;
}

export interface RoadmapLink {
  label: string;
  url: string;
}

export interface RoadmapWarnings {
  pastStartDate: boolean;
  missingEndDate: boolean;
  pastEndDate: boolean;
}

export interface RoadmapLatestWorkstreamUpdate {
  source: string;
  status: string;
  createdAt: string;
}

export interface RoadmapItem {
  key: string;
  title: string;
  description: string;
  status: string | null;
  lifecycle: string | null;
  issueType: RoadmapIssueType;
  parentKey: string | null;
  parentTitle: string | null;
  ancestry: RoadmapAncestryItem[];
  startDate: string | null;
  endDate: string | null;
  deliveryQuarter: string | null;
  planningQuarter: string | null;
  jiraUpdatedAt: string | null;
  assigneeName: string | null;
  assigneeEmail: string | null;
  employeeId: string | null;
  workstreamId: number | null;
  workstreamArchived: boolean | null;
  workstreamOrder: number | null;
  latestWorkstreamUpdate: RoadmapLatestWorkstreamUpdate | null;
  investmentCategory: string | null;
  totalEstHc: number | null;
  quarterEstimates: {
    q1: number | null;
    q2: number | null;
    q3: number | null;
    q4: number | null;
  };
  links: RoadmapLink[];
  narrativeKey: string | null;
  narrativeTitle: string | null;
  subWorkstreamKey: string | null;
  subWorkstreamTitle: string | null;
  ancestorKeys: string[];
  warnings: RoadmapWarnings;
}

export interface RoadmapNarrativeOption {
  key: string;
  title: string;
}

export interface RoadmapDataset {
  items: RoadmapItem[];
  initiatives: RoadmapItem[];
  hierarchyItems: RoadmapItem[];
  narratives: RoadmapNarrativeOption[];
  quarters: string[];
  itemsByKey: Map<string, RoadmapItem>;
  itemsByParentKey: Map<string, RoadmapItem[]>;
  itemsByIssueType: Record<RoadmapIssueType, RoadmapItem[]>;
}

interface ParsedRow {
  jira_key?: string;
  title?: string;
  description?: string;
  status?: string;
  issue_type?: string;
  parent_key?: string;
  ancestry?: string;
  start_date?: string;
  end_date?: string;
  delivery_quarter?: string;
  planning_quarter?: string;
  jira_updated_at?: string;
  assignee_name?: string;
  assignee_email?: string;
  investment_category?: string;
  total_est_hc?: string;
  dev_estimate_q1?: string;
  dev_estimate_q2?: string;
  dev_estimate_q3?: string;
  dev_estimate_q4?: string;
  prd_url?: string;
  ux_design_url?: string;
  tech_spec_url?: string;
  confluence_links?: string;
}

const semicolonDsv = dsvFormat(';');
const DONE_STATUSES = new Set(['Done', 'Completed', 'Cancelled']);
const ROADMAP_ISSUE_TYPES: ReadonlySet<RoadmapIssueType> = new Set([
  'Company Narrative',
  'Narrative Workstream',
  'Sub-Workstream',
  'Roadmap Initiative',
]);

const toNullableString = (value: string | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

const toValidatedDateString = (value: string | undefined): string | null => {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const date = parseDateInput(trimmed);
  return Number.isNaN(date.getTime()) ? null : trimmed;
};

const toValidatedUrl = (value: string | undefined): string | null => {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  try {
    const url = new URL(trimmed);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return null;
    }
    return trimmed;
  } catch {
    return null;
  }
};

const parseNumericValue = (value: string | undefined): number | null => {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/-?\d+(\.\d+)?/);
  return match ? Number(match[0]) : null;
};

const normalizeQuarter = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^(\d{4})[\s-]?Q(\d)$/i);
  if (!match) return null;
  return `${match[1]} Q${match[2]}`;
};

const normalizeItemQuarter = (
  value: string | null | undefined,
  targetQuarter: string
): string | null => {
  const normalized = normalizeQuarter(value);
  if (normalized) return normalized;

  const shortMatch = value?.trim().match(/^Q([1-4])$/i);
  return shortMatch ? `${targetQuarter.slice(0, 4)} Q${shortMatch[1]}` : null;
};

const dateToQuarter = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const date = parseDateInput(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()} Q${Math.floor(date.getMonth() / 3) + 1}`;
};

const quarterToIndex = (quarter: string | null | undefined): number => {
  const normalized = normalizeQuarter(quarter);
  if (!normalized) return Number.MIN_SAFE_INTEGER;
  const [yearText, quarterText] = normalized.split(' Q');
  return Number(yearText) * 4 + (Number(quarterText) - 1);
};

const compareQuarterLabels = (left: string, right: string) =>
  quarterToIndex(left) - quarterToIndex(right);

const parseJsonArray = <T>(value: string | undefined): T[] => {
  const trimmed = value?.trim();
  if (!trimmed || trimmed === '[]') return [];
  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
};

const buildLinks = (
  row: ParsedRow,
  incrementWarning: (key: 'invalidUrlCount' | 'invalidConfluenceLinks') => void
): RoadmapLink[] => {
  const links: RoadmapLink[] = [];

  const directLinks: Array<{label: string; value: string | undefined}> = [
    {label: 'PRD', value: row.prd_url},
    {label: 'UX', value: row.ux_design_url},
    {label: 'Tech spec', value: row.tech_spec_url},
  ];

  directLinks.forEach(({label, value}) => {
    const rawValue = value?.trim();
    const url = toValidatedUrl(value);
    if (rawValue && !url) {
      incrementWarning('invalidUrlCount');
    }
    if (url) {
      links.push({label, url});
    }
  });

  const confluenceLinks = parseJsonArray<unknown>(row.confluence_links);
  confluenceLinks.forEach((entry, index) => {
    if (typeof entry === 'string') {
      const url = toValidatedUrl(entry);
      if (url) {
        links.push({label: `Doc ${index + 1}`, url});
      } else {
        incrementWarning('invalidConfluenceLinks');
      }
      return;
    }

    if (entry && typeof entry === 'object') {
      const maybeUrl =
        'url' in entry && typeof entry.url === 'string'
          ? entry.url
          : 'href' in entry && typeof entry.href === 'string'
            ? entry.href
            : null;
      const maybeLabel =
        'label' in entry && typeof entry.label === 'string'
          ? entry.label
          : 'title' in entry && typeof entry.title === 'string'
            ? entry.title
            : `Doc ${index + 1}`;

      const url = maybeUrl ? toValidatedUrl(maybeUrl) : null;
      if (url) {
        links.push({label: maybeLabel, url});
      } else if (maybeUrl) {
        incrementWarning('invalidConfluenceLinks');
      }
    }
  });

  return links;
};

const normalizeStatus = (value: string | undefined): string | null => {
  const trimmed = value?.trim().replace(/\s+/g, ' ');
  return trimmed ? trimmed : null;
};

const resolveAncestorKeys = (
  key: string,
  itemsByKey: Map<
    string,
    Omit<
      RoadmapItem,
      | 'parentTitle'
      | 'narrativeKey'
      | 'narrativeTitle'
      | 'subWorkstreamKey'
      | 'subWorkstreamTitle'
      | 'ancestorKeys'
    >
  >,
  ancestry: RoadmapAncestryItem[],
  parentKey: string | null
): string[] => {
  const ancestryKeys = ancestry.map((item) => item.key).filter(Boolean);
  if (ancestryKeys.length > 0) {
    return Array.from(new Set(ancestryKeys));
  }

  const visited = new Set<string>([key]);
  const fallbackKeys: string[] = [];
  let currentKey = parentKey;

  while (currentKey && !visited.has(currentKey)) {
    visited.add(currentKey);
    const currentItem = itemsByKey.get(currentKey);
    if (!currentItem) break;
    fallbackKeys.unshift(currentKey);
    currentKey = currentItem.parentKey;
  }

  return fallbackKeys;
};

const getQuarterEstimateValue = (item: RoadmapItem, quarter: string): number => {
  const normalized = normalizeQuarter(quarter);
  if (!normalized) return 0;
  const q = normalized.split(' Q')[1];

  switch (q) {
    case '1':
      return item.quarterEstimates.q1 ?? 0;
    case '2':
      return item.quarterEstimates.q2 ?? 0;
    case '3':
      return item.quarterEstimates.q3 ?? 0;
    case '4':
      return item.quarterEstimates.q4 ?? 0;
    default:
      return 0;
  }
};

export const getQuarterEstimate = (item: RoadmapItem, quarter: string) =>
  getQuarterEstimateValue(item, quarter);

export const getCurrentQuarterLabel = (now = new Date()) =>
  `${now.getFullYear()} Q${Math.floor(now.getMonth() / 3) + 1}`;

export const getDefaultQuarter = (quarters: string[]): string => {
  if (quarters.length === 0) {
    return getCurrentQuarterLabel();
  }

  const currentQuarter = getCurrentQuarterLabel();
  if (quarters.includes(currentQuarter)) {
    return currentQuarter;
  }

  const currentIndex = quarterToIndex(currentQuarter);
  const nextQuarter = [...quarters].find((quarter) => quarterToIndex(quarter) >= currentIndex);
  return nextQuarter ?? quarters[quarters.length - 1];
};

export const isDoneStatus = (status: string | null) => (status ? DONE_STATUSES.has(status) : false);

export const isOverdue = (item: RoadmapItem, now = new Date()) => {
  if (!item.endDate || isDoneStatus(item.status)) return false;
  const endDate = parseDateInput(item.endDate);
  if (Number.isNaN(endDate.getTime())) return false;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return endDate.getTime() < today.getTime();
};

export const matchesNarrativeFilter = (item: RoadmapItem, narrativeKey: string | null) =>
  !narrativeKey || narrativeKey === 'all' ? true : item.narrativeKey === narrativeKey;

export const initiativeMatchesRoadmapQuarter = (item: RoadmapItem, quarter: string | null) => {
  if (!quarter) return true;
  const normalizedQuarter = normalizeQuarter(quarter);
  if (!normalizedQuarter) return true;

  const itemQuarter =
    normalizeItemQuarter(item.deliveryQuarter, normalizedQuarter) ??
    normalizeItemQuarter(item.planningQuarter, normalizedQuarter);
  if (itemQuarter === normalizedQuarter) {
    return true;
  }

  const startQuarter = dateToQuarter(item.startDate);
  const endQuarter = dateToQuarter(item.endDate);
  if (!startQuarter && !endQuarter) {
    return false;
  }

  const targetIndex = quarterToIndex(normalizedQuarter);
  return (
    (!startQuarter || quarterToIndex(startQuarter) <= targetIndex) &&
    (!endQuarter || quarterToIndex(endQuarter) >= targetIndex)
  );
};

export const initiativeMatchesAuditQuarter = (item: RoadmapItem, quarter: string | null) => {
  if (!quarter) return true;
  if (item.status === 'Cancelled') return false;

  if (getQuarterEstimateValue(item, quarter) > 0) {
    return true;
  }

  const startQuarter = dateToQuarter(item.startDate);
  const endQuarter = normalizeQuarter(item.deliveryQuarter);
  const target = normalizeQuarter(quarter);
  if (!target) return true;

  const startsBeforeOrDuring =
    !startQuarter || quarterToIndex(startQuarter) <= quarterToIndex(target);
  const endsAfterOrDuring = !endQuarter || quarterToIndex(endQuarter) >= quarterToIndex(target);

  return startsBeforeOrDuring && endsAfterOrDuring && Boolean(startQuarter || endQuarter);
};

export const buildRoadmapDataset = (rawText: string): RoadmapDataset => {
  const warnings = {
    invalidIssueTypeCount: 0,
    invalidDateCount: 0,
    invalidTimestampCount: 0,
    invalidUrlCount: 0,
    invalidConfluenceLinks: 0,
  };

  const parsedRows = semicolonDsv.parse(rawText) as ParsedRow[];
  const baseItems = parsedRows
    .map((row) => {
      const key = toNullableString(row.jira_key);
      const issueTypeRaw = toNullableString(row.issue_type);
      const issueType =
        issueTypeRaw && ROADMAP_ISSUE_TYPES.has(issueTypeRaw as RoadmapIssueType)
          ? (issueTypeRaw as RoadmapIssueType)
          : null;

      if (!issueTypeRaw || !issueType) {
        if (issueTypeRaw) {
          warnings.invalidIssueTypeCount += 1;
        }
      }

      if (!key || !issueType) {
        return null;
      }

      const startDate = toValidatedDateString(row.start_date);
      const endDate = toValidatedDateString(row.end_date);
      const jiraUpdatedAt = toValidatedDateString(row.jira_updated_at);
      if (row.start_date?.trim() && !startDate) warnings.invalidDateCount += 1;
      if (row.end_date?.trim() && !endDate) warnings.invalidDateCount += 1;
      if (row.jira_updated_at?.trim() && !jiraUpdatedAt) warnings.invalidTimestampCount += 1;

      return {
        key,
        title: row.title?.trim() || key,
        description: row.description?.trim() || '',
        status: normalizeStatus(row.status),
        lifecycle: null,
        issueType,
        parentKey: toNullableString(row.parent_key),
        ancestry: parseJsonArray<RoadmapAncestryItem>(row.ancestry).filter(
          (entry): entry is RoadmapAncestryItem =>
            Boolean(entry && typeof entry.key === 'string' && typeof entry.title === 'string')
        ),
        startDate,
        endDate,
        deliveryQuarter: toNullableString(row.delivery_quarter),
        planningQuarter: toNullableString(row.planning_quarter),
        jiraUpdatedAt,
        assigneeName: toNullableString(row.assignee_name),
        assigneeEmail: toNullableString(row.assignee_email),
        employeeId: null,
        workstreamId: null,
        workstreamArchived: null,
        workstreamOrder: null,
        latestWorkstreamUpdate: null,
        investmentCategory: toNullableString(row.investment_category),
        totalEstHc: parseNumericValue(row.total_est_hc),
        quarterEstimates: {
          q1: parseNumericValue(row.dev_estimate_q1),
          q2: parseNumericValue(row.dev_estimate_q2),
          q3: parseNumericValue(row.dev_estimate_q3),
          q4: parseNumericValue(row.dev_estimate_q4),
        },
        links: buildLinks(row, (warningKey) => {
          warnings[warningKey] += 1;
        }),
        warnings: {
          pastStartDate: false,
          missingEndDate: false,
          pastEndDate: false,
        },
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  const baseItemsByKey = new Map(baseItems.map((item) => [item.key, item]));

  const items: RoadmapItem[] = baseItems.map((item) => {
    const ancestorKeys = resolveAncestorKeys(
      item.key,
      baseItemsByKey,
      item.ancestry,
      item.parentKey
    );
    const ancestorItems = ancestorKeys
      .map((ancestorKey) => baseItemsByKey.get(ancestorKey))
      .filter((ancestor): ancestor is NonNullable<typeof ancestor> => Boolean(ancestor));
    const narrativeItem =
      item.issueType === 'Narrative Workstream'
        ? item
        : (ancestorItems.find((ancestor) => ancestor.issueType === 'Narrative Workstream') ?? null);
    const subWorkstreamItem =
      item.issueType === 'Sub-Workstream'
        ? item
        : (ancestorItems.find((ancestor) => ancestor.issueType === 'Sub-Workstream') ?? null);
    const parentTitle = item.parentKey ? (baseItemsByKey.get(item.parentKey)?.title ?? null) : null;

    return {
      ...item,
      parentTitle,
      narrativeKey: narrativeItem?.key ?? null,
      narrativeTitle: narrativeItem?.title ?? null,
      subWorkstreamKey: subWorkstreamItem?.key ?? null,
      subWorkstreamTitle: subWorkstreamItem?.title ?? null,
      ancestorKeys,
    };
  });

  const initiatives = items.filter((item) => item.issueType === 'Roadmap Initiative');
  const hierarchyItems = items.filter((item) => item.issueType !== 'Roadmap Initiative');
  const narratives = items
    .filter((item) => item.issueType === 'Narrative Workstream')
    .map((item) => ({key: item.key, title: item.title}))
    .sort((left, right) => left.title.localeCompare(right.title));
  const itemsByKey = new Map(items.map((item) => [item.key, item]));
  const itemsByParentKey = items.reduce<Map<string, RoadmapItem[]>>((accumulator, item) => {
    if (!item.parentKey) return accumulator;
    const existing = accumulator.get(item.parentKey) ?? [];
    existing.push(item);
    accumulator.set(item.parentKey, existing);
    return accumulator;
  }, new Map());
  const itemsByIssueType = items.reduce<Record<RoadmapIssueType, RoadmapItem[]>>(
    (accumulator, item) => {
      accumulator[item.issueType].push(item);
      return accumulator;
    },
    {
      'Company Narrative': [],
      'Narrative Workstream': [],
      'Sub-Workstream': [],
      'Roadmap Initiative': [],
    }
  );

  const quarters = Array.from(
    new Set(
      items
        .flatMap((item) => [
          normalizeQuarter(item.deliveryQuarter),
          normalizeQuarter(item.planningQuarter),
          dateToQuarter(item.startDate),
          dateToQuarter(item.endDate),
        ])
        .filter((quarter): quarter is string => Boolean(quarter))
    )
  ).sort(compareQuarterLabels);

  return {
    items,
    initiatives,
    hierarchyItems,
    narratives,
    quarters,
    itemsByKey,
    itemsByParentKey,
    itemsByIssueType,
  };
};

export const buildRoadmapDatasetFromItems = (items: RoadmapItem[]): RoadmapDataset => {
  const initiatives = items.filter((item) => item.issueType === 'Roadmap Initiative');
  const hierarchyItems = items.filter((item) => item.issueType !== 'Roadmap Initiative');
  const narratives = items
    .filter((item) => item.issueType === 'Narrative Workstream')
    .map((item) => ({key: item.key, title: item.title}))
    .sort((left, right) => left.title.localeCompare(right.title));
  const itemsByKey = new Map(items.map((item) => [item.key, item]));
  const itemsByParentKey = items.reduce<Map<string, RoadmapItem[]>>((accumulator, item) => {
    if (!item.parentKey) return accumulator;
    const existing = accumulator.get(item.parentKey) ?? [];
    existing.push(item);
    accumulator.set(item.parentKey, existing);
    return accumulator;
  }, new Map());
  const itemsByIssueType = items.reduce<Record<RoadmapIssueType, RoadmapItem[]>>(
    (accumulator, item) => {
      accumulator[item.issueType].push(item);
      return accumulator;
    },
    {
      'Company Narrative': [],
      'Narrative Workstream': [],
      'Sub-Workstream': [],
      'Roadmap Initiative': [],
    }
  );

  const quarters = Array.from(
    new Set(
      items
        .flatMap((item) => [
          normalizeQuarter(item.deliveryQuarter),
          normalizeQuarter(item.planningQuarter),
          dateToQuarter(item.startDate),
          dateToQuarter(item.endDate),
        ])
        .filter((quarter): quarter is string => Boolean(quarter))
    )
  ).sort(compareQuarterLabels);

  return {
    items,
    initiatives,
    hierarchyItems,
    narratives,
    quarters,
    itemsByKey,
    itemsByParentKey,
    itemsByIssueType,
  };
};
